import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useApi } from '@/lib/api';
import { ORG_TYPE_LABELS } from '@/lib/labels';
import { Button, Card } from '@/components/ui';
import { useOrgContext } from './layout';

export function OrgChildrenPage() {
  const { org, usage, canManage, role } = useOrgContext();
  const api = useApi();
  // Admins here, or of an org above it (no role here); learners don't need the structure.
  const isAdminView = canManage || role === null;
  const children = useQuery({
    queryKey: ['children', org.id],
    queryFn: () => api.children(org.id),
    enabled: isAdminView,
  });
  if (!isAdminView) return <Navigate to=".." replace />;
  const atLimit = Boolean(usage && usage.usage.maxChildOrgs >= usage.limits.maxChildOrgs);

  return (
    <Card
      title="Child organizations"
      action={
        canManage &&
        (atLimit ? (
          <span className="text-sm text-muted-foreground">Plan limit reached</span>
        ) : (
          <Link to="new">
            <Button>Add child organization</Button>
          </Link>
        ))
      }
    >
      {children.data?.orgs.length ? (
        <ul className="divide-y divide-border">
          {children.data.orgs.map((c) => (
            <li key={c.id}>
              <Link
                to={`/organizations/${c.id}`}
                className="flex items-center justify-between py-3 hover:text-primary"
              >
                <span>
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {ORG_TYPE_LABELS[c.type]}
                  </span>
                </span>
                <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No child organizations yet. Add affiliated colleges, departments or teams; they share{' '}
          {org.name}'s plan.
        </p>
      )}
      {canManage && atLimit && (
        <p className="mt-4 text-sm text-muted-foreground">
          Your plan's child organization limit is reached. Contact the Nirvik team to raise it.
        </p>
      )}
    </Card>
  );
}
